import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { LifeBuoy, MessageCircleQuestion, X, ChevronRight, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Page-aware help popover. Floats in the bottom-right of every dashboard page
 * and surfaces contextual FAQs based on the current route + user role. Tells
 * the client exactly what to do when they hit a wall — introducer-portal
 * problems, network building, funding questions, etc.
 *
 * Adding hints for a new route: add a key to HINTS using the route prefix.
 * Longest-prefix-match wins (so "/command-center/intake" beats "/command-center").
 * Add the same key to ROUTE_TOPICS so the popover header shows what page
 * the answers are scoped to.
 */
const HINTS = {
  // Key = route prefix (longest match wins)
  "/command-center/intake": [
    {
      q: "How long does the intake wizard take?",
      a: "Roughly 4–6 minutes for the three steps (Capital Readiness, Capital Safety, Introducer Statements). Your answers save when you click Next — you can leave and come back without losing progress.",
    },
    {
      q: "I'm not ready to disclose my capital amount yet.",
      a: "Select 'Prefer not to say yet' on the capital band question. You can still submit the intake and refine the band later from Settings → Capital Profile. Disclosure becomes mandatory before tier placement on the Deal Room.",
    },
    {
      q: "What does 'Risk capital' actually mean?",
      a: "Capital you can afford to commit without it affecting your living expenses, business operations, debt obligations, or emergency reserves. If any of those apply, choose 'Needed for personal/business expenses' or 'Mixed' — that triggers compliance to slow-walk you for your own protection.",
    },
    {
      q: "Why does Step 3 ask if anyone made specific statements to me?",
      a: "These are the prohibited statements that introducers are contractually barred from making — 'guaranteed returns', 'no risk', 'send funds here', etc. If you check any, your file is automatically flagged for compliance review (and the introducer is investigated). It protects you and us.",
    },
    {
      q: "I clicked Next and nothing happened.",
      a: "Scroll up — there's likely a required field still blank (a red toast usually fires at the top-right). Every question on a step must be answered before Next will advance.",
    },
  ],
  "/command-center": [
    {
      q: "What is the Command Center?",
      a: "Your live onboarding file. It shows your current status across the 17 milestones (intake → KYC → accredited verification → partner onboarding → funded) and surfaces the next action you owe us.",
    },
    {
      q: "What's an ES-VC# and why do I have one?",
      a: "Your unique EchoSense Verification Code — a stable internal reference that follows you through every stage. Quote it in support emails so we can pull your file instantly.",
    },
    {
      q: "Why am I stuck on 'kyc_pending'?",
      a: "Required documents haven't all been uploaded or approved yet. Open Documents in the sidebar; the checklist shows exactly what's missing or rejected.",
    },
  ],
  "/how-to-invest": [
    {
      q: "My wire hasn't appeared in the portal yet.",
      a: "Wires typically settle in 1–2 business days. Make sure your client reference (top of this page) was included on the wire. If it's been 48 hours and nothing shows up, email admin@echosensecapital.com with the wire confirmation.",
    },
    {
      q: "Why can't I fund with a card right now?",
      a: "Paytiko is currently in demo mode while we finalise API credentials. Until it's live, all funding goes through the JV wire flow above. Paytiko will activate with zero change to your experience once keys are provisioned.",
    },
  ],
  "/funding": [
    {
      q: "What are the three funding steps?",
      a: "Step 1 — sign the Joint Venture Funding Agreement (SIGNED on signup). Step 2 — your dedicated Partner Account is provisioned by the partner (24-business-hour SLA). Step 3 — you wire to that account; funds settle in 1–2 business days.",
    },
    {
      q: "Why can't I just send money straight to my Partner Account?",
      a: "Compliance: the partner requires the EchoSense JV Agreement on file before they can open the account in your name. The portal automates the handoff for you — you don't need to contact the partner directly until the account is live.",
    },
    {
      q: "How will I know my Partner Account is ready?",
      a: "You'll get an email + a portal notification with the account number, routing/IBAN/SWIFT details, and a reference code to include on the wire. The Funding page also flips Step 2 to 'PROVISIONED' at that point.",
    },
  ],
  "/lead-management": [
    {
      q: "I'm not seeing my referred leads in the pipeline.",
      a: "Leads only appear in the pipeline after admin qualifies them. Your introducer dashboard shows them immediately. If a lead is missing after 24h, ping an admin via the Contact page.",
    },
    {
      q: "How do I hand a cold lead back to the pool?",
      a: "Open the lead card → Status → set to 'Cold' and uncheck 'Locked'. It'll be reassigned by an admin.",
    },
  ],
  "/dashboard": [
    {
      q: "I completed KYC but my account still says 'pending'.",
      a: "KYC approval is manual (1 business day). If it's been longer, click Contact in the footer and include your email.",
    },
    {
      q: "How is my 'SCALING UP' badge calculated?",
      a: "It's your combined net P&L across Native Fund + Partner. When it's positive you're scaling — tier upgrades trigger a celebration at each capital band (Pillar / Vanguard / Sovereign).",
    },
    {
      q: "What are the capital bands?",
      a: "Pillar ≥ $250K, Vanguard ≥ $500K, Sovereign ≥ $1M combined. Crossing a band fires a one-time confetti celebration + logs a note to your admin timeline.",
    },
  ],
  "/kyc-verification": [
    {
      q: "What's the difference between this page and 'Documents'?",
      a: "This page (Verification & Accreditation) drives the live SumSub identity check + accredited-investor review — fully automated once SumSub keys are wired. 'Documents' is the manual upload fallback for documents (passport, proof of address, bank statement, selfie). Use the manual flow until SumSub goes live.",
    },
    {
      q: "I clicked 'Start KYC with SumSub' and nothing visible happened.",
      a: "SumSub credentials are still pending provisioning. Your session was queued and your admin was notified to advance you manually. In the meantime, upload your documents via the 'Documents' sidebar item — that path is fully live.",
    },
    {
      q: "What counts as accredited / qualified investor proof?",
      a: "US: a Reg-D letter from your CPA/attorney OR documented $1M+ net worth (excluding primary residence) OR $200K+/yr income for two consecutive years. International: equivalent jurisdictional certification. Upload from the Verification Portal sidebar item.",
    },
  ],
  "/kyc": [
    {
      q: "What document quality is acceptable?",
      a: "Clear, color, all four corners visible, no glare, file under 10MB. PDF/JPG/PNG only. Government ID must show full name + DOB + expiry. Proof of address must be dated within the last 90 days (utility bill, bank statement, government letter).",
    },
    {
      q: "How long does KYC review take?",
      a: "1 business day. You'll get an email + portal notification when approved or rejected. Rejected docs come with a specific reason — re-upload from the same checklist row.",
    },
    {
      q: "My document upload is stuck.",
      a: "Files must be PDF/JPG/PNG under 10MB. Remove metadata or re-export if uploads stall. If it keeps failing, email the doc to admin@echosensecapital.com with your client ID.",
    },
    {
      q: "What's a 'Selfie with ID' and why is it optional?",
      a: "A photo of you holding your government ID next to your face. It's optional today because SumSub will capture it automatically once live — but uploading one now speeds up your accreditation review.",
    },
  ],
  "/dealflow": [
    {
      q: "Why can't I see all the deals?",
      a: "Deal visibility is tiered by capital readiness (Standard / Institutional / HNW / Sovereign). Higher tiers unlock as your asset-manager-funded balance crosses each band. The tier badge at the top of this page shows your current access level.",
    },
    {
      q: "I acknowledged a deal — what happens next?",
      a: "Your acknowledgment is logged with a timestamp for compliance. An admin reviews and either invites you to a 1:1 to discuss the allocation, or moves the deal to your watchlist. Either way, you'll see a status change in the deal card within 1 business day.",
    },
  ],
  "/partner-funding": [
    {
      q: "When do my funding instructions appear here?",
      a: "After the partner countersigns your participation agreement. Until then this page shows a pending state — never send funds based on instructions from any other channel.",
    },
    {
      q: "What is the 1.2% incoming charge?",
      a: "The partner's receivable bank account (or any alternative payment instructions) carries a 1.2% incoming charge, borne entirely by the participant. It is disclosed on this page and in your agreement.",
    },
    {
      q: "I sent the funds — what now?",
      a: "Upload your proof of payment on this page (transaction hash and/or a receipt). The team confirms receipt, your Partner Account is mapped, and your dashboard is activated.",
    },
  ],
  "/partner-account": [
    {
      q: "My Partner Account isn't showing a balance yet.",
      a: "Either the account hasn't been provisioned yet (Funding Step 2 still 'Pending provision') or your wire is still in transit (1–2 business days from send). Check the Funding page for live status.",
    },
    {
      q: "Can I withdraw directly from my Partner Account?",
      a: "Withdrawals run through the EchoSense JV reconciliation process — request a withdrawal here and we coordinate the transfer back to your bank of record. Direct withdrawals from the Partner Account are blocked by the JV agreement.",
    },
  ],
  "/messages": [
    {
      q: "How fast does admin respond?",
      a: "Same business day for client messages, within 4 business hours for compliance-flagged threads. After-hours messages get a same-next-morning reply.",
    },
  ],
  "/verification": [
    {
      q: "Why is accredited verification separate from KYC?",
      a: "KYC proves who you are; accredited verification proves your investor-eligibility band. Both are regulator-mandated before any deal-room placement, and they're scored separately for audit trail purposes.",
    },
  ],
  "/admin/command-center": [
    {
      q: "Which milestone do I advance when a client wires funds?",
      a: "Advance to 'funded' (Step 4). Don't forget to set the wire amount in the client's capital_actual field — it drives band placement.",
    },
    {
      q: "When should I fire a Red Alert?",
      a: "Use it when a client says or does something that triggers compliance concern — references to guaranteed returns, requests to bypass the JV process, or stalled files >7 days with no response. The Red Alert routes to founder + compliance instantly.",
    },
  ],
  "/admin/dealflow": [
    {
      q: "How do I override a client's deal tier?",
      a: "Open the client row → Tier Override → pick the new tier. The override is logged with your admin ID + timestamp. The system auto-recalculates from capital intake unless an override is active.",
    },
  ],
  "/admin/activity-log": [
    {
      q: "What does 'signup_abandoned' mean?",
      a: "A user started checkout (pending_registration created) but didn't complete payment within 60 minutes. The background sweep marks these stale every 5 minutes. Check the Drop-off Insights widget to see which promo code or VC# referrer is the worst offender.",
    },
  ],
  "/pioneers": [
    {
      q: "What's the Pioneer program?",
      a: "Founding members №001–500. Pioneers get a permanent badge, locked-in $99/mo subscription rate, priority deal-room access, and a place on the Wall of Champions. The crown badge in the header is your Pioneer number.",
    },
  ],
  "/lifestyle": [
    {
      q: "How are lifestyle products selected?",
      a: "Curated for HNW members — private aviation, yacht charter, fine art advisory, concierge medical. Each partner is vetted for service quality and discreet handling. Contact admin for introductions; nothing is transactional inside this portal.",
    },
  ],
  "/referrer": [
    {
      q: "Nobody is clicking my referral link.",
      a: "Introducers who share in 1:1 conversations (not bulk DMs) convert ~4× better. Lead with an invite to a Calendly intro rather than a cold link.",
    },
    {
      q: "My tier-2 count is zero.",
      a: "Tier-2 counts only when your direct referrals bring their own referrals in. If they're still prospecting, be patient — and introduce them to the How to Invest page to shorten their cycle.",
    },
  ],
};

// Human-readable topic label shown in the popover header so members see
// at a glance which page these answers are scoped to.
const ROUTE_TOPICS = {
  "/command-center/intake": "Onboarding Intake",
  "/command-center": "Command Center",
  "/how-to-invest": "How to Invest",
  "/funding": "Funding & Wires",
  "/lead-management": "Lead Pipeline",
  "/dashboard": "Your Dashboard",
  "/kyc-verification": "Verification & Accreditation",
  "/kyc": "Documents",
  "/dealflow": "Deal Room",
  "/partner-funding": "Funding Status",
  "/partner-account": "Partner Account",
  "/messages": "Messages",
  "/verification": "Accredited Verification",
  "/admin/command-center": "Command Center",
  "/admin/dealflow": "Deal Room",
  "/admin/activity-log": "Member Activity Log",
  "/pioneers": "Pioneers · Wall of Champions",
  "/lifestyle": "Lifestyle",
  "/referrer": "Introducer Program",
};

const DEFAULT_HINTS = [
  {
    q: "Something on the portal is broken or confusing.",
    a: "Please email admin@echosensecapital.com with a screenshot and the URL you're on. We triage within one business day.",
  },
  {
    q: "Where can I talk to a human?",
    a: "Use the live-chat widget in the bottom-right corner, or book a slot via the Consultation page.",
  },
];

const matchRouteKey = (pathname) => {
  // Longest-prefix-match so "/command-center/intake" beats "/command-center"
  const keys = Object.keys(HINTS).sort((a, b) => b.length - a.length);
  return keys.find((k) => pathname.startsWith(k)) || null;
};

const getHintsForPath = (pathname, role) => {
  // Role-specific overlay: introducers see referrer hints on their dashboard
  if (role === "referrer" && pathname.startsWith("/dashboard")) {
    return [...(HINTS["/referrer"] || []), ...(HINTS["/dashboard"] || [])];
  }
  const key = matchRouteKey(pathname);
  return key ? HINTS[key] : DEFAULT_HINTS;
};

const getTopicForPath = (pathname, role) => {
  if (role === "referrer" && pathname.startsWith("/dashboard")) {
    return "Your Dashboard · Introducer";
  }
  const key = matchRouteKey(pathname);
  return key ? ROUTE_TOPICS[key] : "General help";
};

const HelpPopover = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);

  const hints = useMemo(() => getHintsForPath(pathname, user?.role), [pathname, user?.role]);
  const topic = useMemo(() => getTopicForPath(pathname, user?.role), [pathname, user?.role]);

  // Hide on public routes (landing, login, register, etc.)
  const isDashboardRoute = user && !["/login", "/register", "/", "/about", "/consultation", "/contact"].some(
    (p) => pathname === p
  );
  if (!isDashboardRoute) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white shadow-lg shadow-blue-600/30 hover:scale-105 transition-transform flex items-center justify-center"
          data-testid="help-popover-trigger"
          aria-label="Need help?"
        >
          <LifeBuoy size={22} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0 bg-white border border-slate-200 shadow-xl"
        align="end"
        side="top"
        sideOffset={12}
        data-testid="help-popover"
      >
        <div className="p-4 bg-gradient-to-br from-blue-600 to-emerald-500 text-white rounded-t-md flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold flex items-center gap-1.5">
              <MessageCircleQuestion size={16} /> Need help?
            </p>
            <p className="text-xs text-white/80 mt-0.5">Answers for what you're looking at right now.</p>
            <Badge
              className="mt-2 bg-white/15 hover:bg-white/15 text-white border border-white/25 text-[10px] uppercase tracking-wider font-medium"
              data-testid="help-popover-topic"
            >
              {topic}
            </Badge>
          </div>
          <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white shrink-0" aria-label="Close help">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {hints.map((h, i) => {
            const isOpen = activeIdx === i;
            const stableKey = `${h.title || h.label || 'hint'}-${(h.body || '').slice(0, 24)}-${i}`;
            return (
              <div
                key={stableKey}
                className="border-b border-slate-100 last:border-0"
                data-testid={`help-item-${i}`}
              >
                <button
                  onClick={() => setActiveIdx(isOpen ? null : i)}
                  className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors"
                >
                  <ChevronRight
                    size={14}
                    className={`text-slate-400 mt-1 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                  <span className="text-sm font-medium text-slate-900 flex-1">{h.q}</span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pl-11 text-sm text-slate-600 leading-relaxed" data-testid={`help-answer-${i}`}>
                    {h.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
          <span>Still stuck?</span>
          <a
            href="mailto:admin@echosensecapital.com"
            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
            data-testid="help-email-link"
          >
            Email admin <ExternalLink size={12} />
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default HelpPopover;
